import { useEffect, useState } from 'react'
import { useHighlightsApi } from './useHighlightsApi'

// Cache key used in sessionStorage. Bump if the payload shape changes.
const CACHE_PREFIX = 'tbtv:highlights:moment-types:v1:'
const CACHE_TTL_MS = 10 * 60 * 1000  // 10 min

// In-memory cache mirrors sessionStorage to skip parse cost on hot renders.
const MEM = new Map()

function readCache(profileKey) {
  const memHit = MEM.get(profileKey)
  if (memHit && Date.now() < memHit.exp) return memHit.value
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + profileKey)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (Date.now() >= parsed.exp) return null
    MEM.set(profileKey, parsed)
    return parsed.value
  } catch {
    return null
  }
}

function writeCache(profileKey, value) {
  const entry = { value, exp: Date.now() + CACHE_TTL_MS }
  MEM.set(profileKey, entry)
  try { sessionStorage.setItem(CACHE_PREFIX + profileKey, JSON.stringify(entry)) }
  catch { /* private mode / quota — harmless */ }
}

// Fallback that mirrors migration 0035's hockey seed exactly, so a first
// paint before the fetch resolves — or a network failure — still renders
// the correct chip vocabulary in the original order and colors. Keeps the
// UI byte-identical to pre-Phase-3 behavior on any resolve failure.
const HOCKEY_FALLBACK = [
  { key: 'goal',       label: 'Goals',       color: '#22c55e', sort_order: 0 },
  { key: 'save',       label: 'Saves',       color: '#3b82f6', sort_order: 1 },
  { key: 'shot',       label: 'Shots',       color: '#a78bfa', sort_order: 2 },
  { key: 'fight',      label: 'Fights',      color: '#fb923c', sort_order: 3 },
  { key: 'penalty',    label: 'Penalties',   color: '#facc15', sort_order: 4 },
  { key: 'power_play', label: 'Power play',  color: '#a855f7', sort_order: 5 },
  { key: 'defense',    label: 'Defense',     color: '#94a3b8', sort_order: 6 },
  { key: 'narrative',  label: 'Narrative',   color: '#fbbf24', sort_order: 7 },
  { key: 'structural', label: 'Structural',  color: '#64748b', sort_order: 8 },
  { key: '(other)',    label: 'Other',       color: '#666',    sort_order: 9 },
]

// Fetch (and cache) the chip vocabulary for a sport profile.
// - profileKey: 'hockey' | 'football' | future
// - returns { momentTypes, loading, error }
// - momentTypes is always usable — falls back to HOCKEY_FALLBACK until the
//   fetch resolves, and stays on it on any failure (never null).
export function useProfileMomentTypes(profileKey) {
  const api = useHighlightsApi()
  const key = profileKey || 'hockey'
  const [state, setState] = useState(() => ({
    momentTypes: readCache(key) || HOCKEY_FALLBACK,
    loading: !readCache(key),
    error: null,
  }))

  useEffect(() => {
    let cancelled = false
    const cached = readCache(key)
    if (cached) {
      setState({ momentTypes: cached, loading: false, error: null })
      return () => { cancelled = true }
    }
    setState(s => ({ ...s, loading: true, error: null }))
    api.getProfileMomentTypes(key)
      .then(res => {
        if (cancelled) return
        const list = Array.isArray(res?.moment_types) ? res.moment_types : []
        const value = list.length ? list : HOCKEY_FALLBACK
        writeCache(key, value)
        setState({ momentTypes: value, loading: false, error: null })
      })
      .catch(err => {
        if (cancelled) return
        setState({ momentTypes: HOCKEY_FALLBACK, loading: false, error: err })
      })
    return () => { cancelled = true }
  }, [api, key])

  return state
}

// Explicit cache-buster (Phase 4 admin panel will call this after PATCH).
export function invalidateProfileMomentTypesCache(profileKey) {
  if (profileKey) {
    MEM.delete(profileKey)
    try { sessionStorage.removeItem(CACHE_PREFIX + profileKey) } catch {}
  } else {
    MEM.clear()
    try {
      // Clear all keys with our prefix.
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i)
        if (k && k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k)
      }
    } catch {}
  }
}
